import { z } from "zod";
import {
  orderSummarySchema,
  orderDetailSchema,
  ordersPageSchema,
  receiptInputSchema,
  type OrderStatus,
  type OrderSummary,
  type OrderDetail,
} from "cadendar-shared/ordering/receipts";
export type { OrderStatus, OrderSummary, OrderDetail };

const catalogStatusSchema = z.enum(["master", "proposed"]);

const supplierSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

const catalogOfferSchema = z.object({
  offerId: z.string().uuid(),
  productId: z.string().uuid(),
  brand: z.string(),
  commercialName: z.string(),
  manufacturerReference: z.string(),
  catalogStatus: catalogStatusSchema,
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  supplierReference: z.string(),
  catalogPriceCents: z.number().int().nonnegative(),
  keywords: z.array(z.string()),
});

const keywordSchema = z.object({
  name: z.string(),
});

const catalogBrandSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  aliases: z.array(z.string()),
});

const brandSuggestionSchema = z.object({
  name: z.string(),
});

const brandNormalizationConflictSchema = z.object({
  id: z.string().uuid(),
  canonicalBrandName: z.string(),
  manufacturerReference: z.string(),
  firstProduct: z.object({
    id: z.string().uuid(),
    brand: z.string(),
    commercialName: z.string(),
  }),
  secondProduct: z.object({
    id: z.string().uuid(),
    brand: z.string(),
    commercialName: z.string(),
  }),
});

const megadentalInvoiceLineSchema = z.object({
  supplierReference: z.string(),
  brand: z.string(),
  commercialName: z.string(),
  catalogPriceCents: z.number().int().positive(),
  orderedQuantity: z.number().int().positive(),
  deliveredQuantity: z.number().int().nonnegative(),
  invoicedUnitPriceCents: z.number().int().positive().nullable(),
  invoicedTotalCents: z.number().int().positive().nullable(),
  isOutOfStock: z.boolean(),
  existing: z.boolean(),
});

const megadentalInvoicePreviewSchema = z.object({
  invoiceNumber: z.string().nullable(),
  invoiceDate: z.string().nullable(),
  orderedAt: z.string().nullable(),
  lines: z.array(megadentalInvoiceLineSchema),
});

const megadentalImportResultSchema = z.object({
  createdCount: z.number().int().nonnegative(),
  linkedCount: z.number().int().nonnegative(),
  orderId: z.string().uuid(),
});

const cartLineSchema = catalogOfferSchema.extend({
  quantity: z.number().int().positive(),
  updatedAt: z.string(),
});

const createProductSchema = z
  .object({
    brand: z.string().trim().min(1),
    commercialName: z.string().trim().min(1),
    manufacturerReference: z.string().trim().default(""),
    keywords: z.array(z.string()).default([]),
    offers: z
      .array(
        z.object({
          supplierId: z.string().uuid(),
          supplierReference: z.string().trim().min(1),
          catalogPriceCents: z
            .number()
            .int()
            .nonnegative()
            .max(2147483647)
            .default(0),
        }),
      )
      .min(1),
    cart: z
      .object({
        offerIndex: z.number().int().nonnegative(),
        quantity: z.number().int().positive().max(2147483647),
      })
      .nullable()
      .optional(),
  })
  .refine(
    (product) =>
      !product.cart || product.cart.offerIndex < product.offers.length,
    {
      message: "Choisissez un fournisseur pour la liste de courses.",
      path: ["cart", "offerIndex"],
    },
  );

export type CreateProductInput = z.infer<typeof createProductSchema>;

const orderAuthorSchema = z.object({
  id: z.string(),
  fullname: z.string(),
});

const positiveQuantitySchema = z.number().int().positive().max(2147483647);
const updateOrderSchema = z.object({
  lines: z
    .array(
      z.union([
        z
          .object({
            orderLineId: z.string().uuid(),
            quantity: positiveQuantitySchema,
          })
          .strict(),
        z
          .object({
            offerId: z.string().uuid(),
            quantity: positiveQuantitySchema,
          })
          .strict(),
      ]),
    )
    .min(1),
});
const updateReceiptSchema = receiptInputSchema;

export type UpdateOrderInput = z.infer<typeof updateOrderSchema>;
export type UpdateReceiptInput = z.infer<typeof updateReceiptSchema>;

export type Supplier = z.infer<typeof supplierSchema>;
export type CatalogOffer = z.infer<typeof catalogOfferSchema>;
export type CartLine = z.infer<typeof cartLineSchema>;
export type MegadentalInvoiceLine = z.infer<typeof megadentalInvoiceLineSchema>;
export type CatalogKeyword = z.infer<typeof keywordSchema>;
export type CatalogBrand = z.infer<typeof catalogBrandSchema>;
export type BrandSuggestion = z.infer<typeof brandSuggestionSchema>;
export type BrandNormalizationConflict = z.infer<
  typeof brandNormalizationConflictSchema
>;

const ordersApiBaseUrl = "/cadendarOrders";

async function request(path: string, options?: RequestInit) {
  const headers = new Headers(options?.headers);
  if (!(options?.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(`${ordersApiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
    headers,
  });
  if (!response.ok) {
    if (response.status === 401) window.location.reload();
    const body = await response.json().catch(() => null);
    const message =
      typeof body === "object" &&
      body !== null &&
      "error" in body &&
      typeof body.error === "string"
        ? body.error
        : "La requête de commande a échoué.";
    throw new Error(message);
  }
  return response.json();
}

function productPath(productId: string) {
  return `/ordering/catalog/products/${encodeURIComponent(productId)}`;
}

function offerPath(offerId: string) {
  return `/ordering/catalog/offers/${encodeURIComponent(offerId)}`;
}

function orderPath(orderId: string) {
  return `/ordering/orders/${encodeURIComponent(orderId)}`;
}

export const orderingApi = {
  async searchCatalog(query: string, supplierId?: string) {
    const parameters = new URLSearchParams({ query });
    if (supplierId) parameters.set("supplierId", supplierId);
    return z
      .array(catalogOfferSchema)
      .parse(await request(`/ordering/catalog?${parameters.toString()}`));
  },
  async listKeywords(query: string) {
    const parameters = new URLSearchParams({ query });
    return z
      .array(keywordSchema)
      .parse(
        await request(`/ordering/catalog/keywords?${parameters.toString()}`),
      );
  },
  async createKeyword(name: string) {
    return keywordSchema.parse(
      await request("/ordering/catalog/keywords", {
        method: "POST",
        body: JSON.stringify({ name }),
      }),
    );
  },
  async listBrands() {
    return z
      .array(catalogBrandSchema)
      .parse(await request("/ordering/catalog/brands"));
  },
  async listBrandSuggestions(query: string) {
    const parameters = new URLSearchParams({ query });
    return z
      .array(brandSuggestionSchema)
      .parse(
        await request(
          `/ordering/catalog/brands/suggestions?${parameters.toString()}`,
        ),
      );
  },
  async createBrand(name: string) {
    return catalogBrandSchema.parse(
      await request("/ordering/catalog/brands", {
        method: "POST",
        body: JSON.stringify({ name }),
      }),
    );
  },
  async updateBrand(brandId: string, name: string) {
    await request(`/ordering/catalog/brands/${encodeURIComponent(brandId)}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    });
  },
  async createBrandAlias(brandId: string, name: string) {
    return catalogBrandSchema.parse(
      await request(
        `/ordering/catalog/brands/${encodeURIComponent(brandId)}/aliases`,
        {
          method: "POST",
          body: JSON.stringify({ name }),
        },
      ),
    );
  },
  async deleteBrandAlias(brandId: string, aliasName: string) {
    await request(
      `/ordering/catalog/brands/${encodeURIComponent(brandId)}/aliases/${encodeURIComponent(aliasName)}`,
      { method: "DELETE" },
    );
  },
  async listBrandNormalizationConflicts() {
    return z
      .array(brandNormalizationConflictSchema)
      .parse(await request("/ordering/catalog/brands/conflicts"));
  },
  async listSuppliers() {
    return z.array(supplierSchema).parse(await request("/ordering/suppliers"));
  },
  async createSupplier(name: string) {
    return supplierSchema.parse(
      await request("/ordering/suppliers", {
        method: "POST",
        body: JSON.stringify({ name }),
      }),
    );
  },
  async createProduct(
    options: CreateProductInput & {
      catalogStatus: "master" | "proposed";
    },
  ) {
    const { catalogStatus, ...input } = options;
    const body = createProductSchema.parse(input);
    const path =
      catalogStatus === "master"
        ? "/ordering/catalog/master"
        : "/ordering/catalog/proposals";
    return z
      .array(catalogOfferSchema)
      .parse(
        await request(path, { method: "POST", body: JSON.stringify(body) }),
      );
  },
  async previewMegadentalInvoice(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    return megadentalInvoicePreviewSchema.parse(
      await request("/ordering/catalog/imports/megadental/preview", {
        method: "POST",
        body: formData,
      }),
    );
  },
  async importMegadentalInvoice(input: {
    invoiceNumber: string;
    invoiceDate: string;
    orderedAt: string;
    lines: Array<{
      supplierReference: string;
      brand: string;
      commercialName: string;
      manufacturerReference: string;
      keywords: string[];
      catalogPriceCents: number;
      orderedQuantity: number;
      deliveredQuantity: number;
      invoicedUnitPriceCents: number | null;
      invoicedTotalCents: number | null;
    }>;
  }) {
    return megadentalImportResultSchema.parse(
      await request("/ordering/catalog/imports/megadental", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    );
  },
  async promoteProduct(productId: string) {
    await request(`${productPath(productId)}/promote`, { method: "POST" });
  },
  async mergeCatalogProducts(targetProductId: string, sourceProductId: string) {
    await request(
      `${productPath(targetProductId)}/merge/${encodeURIComponent(sourceProductId)}`,
      { method: "POST" },
    );
  },
  async updateProduct(options: {
    productId: string;
    brand: string;
    commercialName: string;
    manufacturerReference: string;
    keywords: string[];
  }) {
    const { productId, ...body } = options;
    await request(productPath(productId), {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  async updateOffer(options: {
    offerId: string;
    supplierReference: string;
    catalogPriceCents: number;
  }) {
    const { offerId, ...body } = options;
    await request(offerPath(offerId), {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  async getCart() {
    return z.array(cartLineSchema).parse(await request("/ordering/cart"));
  },
  async setCartQuantity(options: { offerId: string; quantity: number }) {
    await request(`/ordering/cart/${encodeURIComponent(options.offerId)}`, {
      method: "PUT",
      body: JSON.stringify({ quantity: options.quantity }),
    });
  },
  async createOrdersFromCart() {
    return z
      .array(orderSummarySchema)
      .parse(await request("/ordering/cart", { method: "POST" }));
  },
  async listOrders({
    authorId,
    offset = 0,
  }: {
    authorId?: string;
    offset?: number;
  }) {
    const parameters = new URLSearchParams();
    if (authorId) parameters.set("authorId", authorId);
    parameters.set("offset", String(offset));
    return ordersPageSchema.parse(
      await request(`/ordering/orders?${parameters.toString()}`),
    );
  },
  async listOrderAuthors() {
    return z
      .array(orderAuthorSchema)
      .parse(await request("/ordering/orders/authors"));
  },
  async getOrder(orderId: string) {
    return orderDetailSchema.parse(await request(orderPath(orderId)));
  },
  async updateOrder(options: UpdateOrderInput & { orderId: string }) {
    const body = updateOrderSchema.parse({ lines: options.lines });
    return orderDetailSchema.parse(
      await request(orderPath(options.orderId), {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    );
  },
  async updateReceipt(
    options: UpdateReceiptInput & { orderId: string; receiptId: string },
  ) {
    const body = updateReceiptSchema.parse({ lines: options.lines });
    return orderDetailSchema.parse(
      await request(
        `${orderPath(options.orderId)}/receipts/${encodeURIComponent(options.receiptId)}`,
        { method: "PATCH", body: JSON.stringify(body) },
      ),
    );
  },
  async createReceipt(options: {
    orderId: string;
    lines: {
      orderLineId: string;
      receivedQuantity: number;
      paidUnitPriceCents: number;
    }[];
  }) {
    const { orderId } = options;
    const body = receiptInputSchema.parse({ lines: options.lines });
    return orderDetailSchema.parse(
      await request(`${orderPath(orderId)}/receipts`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    );
  },
};
